import { Controller } from '@hotwired/stimulus'
import { get, post } from '../lib/request'

const URL = '/bare/mockup'
const DOWNLOAD_URL = '/admin/mockup_download'

export default class extends Controller {
  static targets = [
    'iframe',
    'productSelector',
    'backgroundSelector',
    'downloadSelector',
    'backgroundToggle',
    'productToggle',
    'downloadToggle',
  ]

  static values = {
    color: { type: String, default: 'blackFrame' },
    texture: { type: String, default: 'white' },
    plus: { type: Boolean, default: false },
    productId: Number,
    scale: Number,
  }

  iframeTargetConnected = (element) => {
    const parent = element.parentElement
    const parentWidth = parent.clientWidth
    const parentHeight = parent.clientHeight

    // Calculate dimensions for 9:16 aspect ratio (width:height = 9:16)
    const aspectRatio = 9 / 16

    let iframeWidth, iframeHeight

    // Check if parent width or height is the limiting factor
    if (parentWidth / parentHeight > aspectRatio) {
    // Parent is wider than our aspect ratio, so height is the limiting factor
      iframeHeight = parentHeight
      iframeWidth = parentHeight * aspectRatio
    }
    else {
    // Parent is taller than our aspect ratio, so width is the limiting factor
      iframeWidth = parentWidth
      iframeHeight = parentWidth / aspectRatio
    }

    // Apply dimensions to the iframe
    element.style.width = `${iframeWidth}px`
    element.style.height = `${iframeHeight}px`

    this.scaleValue = iframeWidth / 720
    this.setIframeSrc()
  }

  backgroundToggleTargetConnected = () => {
    this.setFunctions('background', ['product', 'download'])
  }

  productToggleTargetConnected = () => {
    this.setFunctions('product', ['background', 'download'])
  }

  downloadToggleTargetConnected = () => {
    this.setFunctions('download', ['background', 'product'])
  }

  connect = () => {
    if (this.hasBackgroundToggleTarget) {
      window.addEventListener('showMockupsPreview', () => {
        this.toggleTargets.forEach(toggle => toggle.close())
      })
    }
  }

  disconnect = () => {
    if (this.hasBackgroundToggleTarget) {
      window.removeEventListener('showMockupsPreview', () => {
        this.toggleTargets.forEach(toggle => toggle.close())
      })
    }
  }

  download = () => {
    this.downloadToggleTarget.classList.add('pulse-animation')

    const params = {
      product_id: this.productIdValue,
      bg_image: this.textureValue,
      frame_color: this.colorValue,
    }

    post(DOWNLOAD_URL, { body: JSON.stringify(params), responseKind: 'turbo-stream' })
  }

  changeFrame = (event) => {
    this.colorValue = event.target.dataset.mockupsFrame

    this.setIframeSrc()
  }

  swapImage = (event) => {
    const id = event.params.id
    this.selectedProduct = document.getElementById(`mockup-product-${this.productIdValue}`)
    this.selectedProduct.style.background = 'white'
    this.selectedProduct.style.color = 'inherit'

    this.productIdValue = id
    this.selectedProduct = event.target.closest('a')
    this.selectedProduct.style.background = '#555'
    this.selectedProduct.style.color = 'white'

    this.setIframeSrc()
  }

  changeTexture = ({ params: { texture } }) => {
    this.textureValue = texture

    this.setIframeSrc()
  }

  setIframeSrc = () => {
    const data = {
      product_id: this.productIdValue,
      bg_image: this.textureValue,
      frame_color: this.colorValue,
      scale: this.scaleValue,
      plus: this.plusValue,
    }
    const params = new URLSearchParams(data).toString()
    const url = `${URL}?${params}`

    this.iframeTarget.src = url
  }

  async getDataUrl() {
    const url = `${URL}?product_id=${this.productIdValue}&asset_id=${this.textureValue}`
    const response = await get(url, { responseKind: 'json' })

    if (response.ok) {
      const json = await response.json

      this.convertedImage = json.product_image
      this.convertedTexture = json.asset_image
    }
  }

  toggle = ({ params: { value } }) => {
    const toggle = this[`${value}ToggleTarget`]

    if (toggle.isOpen()) {
      toggle.close()
    }
    else {
      toggle.open()
    }
  }

  setFunctions = (current, opposites) => {
    const toggle = this[`${current}ToggleTarget`]
    const selector = this[`${current}SelectorTarget`]
    const svg = toggle.getElementsByTagName('svg')[0]

    toggle.open = () => {
      selector.style.maxHeight = '100%'
      svg.style.transition = 'transform .4s ease-in-out'
      svg.style.transform = 'rotate(180deg)'

      opposites.forEach((opposite) => {
        const upperOpposite = opposite.charAt(0).toUpperCase() + opposite.slice(1)

        if (this[`has${upperOpposite}ToggleTarget`]) {
          const oppositeToggle = this[`${opposite}ToggleTarget`]
          const oppositeSelector = this[`${opposite}SelectorTarget`]
          const oppositeSvg = oppositeToggle.getElementsByTagName('svg')[0]
          oppositeSelector.style.maxHeight = '0px'
          oppositeSvg.style.transform = 'rotate(0)'
        }
      })
    }

    toggle.close = () => {
      selector.style.maxHeight = '0px'
      svg.style.transform = 'rotate(0)'
    }
    toggle.isOpen = () => {
      return selector.style.maxHeight === '100%'
    }
  }

  get toggleTargets() {
    return [
      this.backgroundToggleTarget,
      this.productToggleTarget,
      this.downloadToggleTarget,
    ]
  }
}
